import { ChevronDown } from 'lucide-react';
import { useState, useEffect } from "react";
import { AnimatedText, AnimatedTitle } from "@/components/ui/AnimatedText";
import ParallaxImage from "@/components/ui/ParallaxImage";
import { apiFetch } from "@/utils/api";
import { useSEO } from "@/utils/useSEO";
import { useTranslation } from "react-i18next";

export default function Facilities() {
  const { t } = useTranslation();
  useSEO(
    "Facilities",
    "Enjoy world-class facilities at Patal Kikian Villas Sidemen including an infinity pool overlooking Mount Agung, Balinese spa, open-air restaurant with authentic cuisine, and lush tropical gardens surrounded by rice terraces."
  );
  const [facilities, setFacilities] = useState<any[]>([]);

  useEffect(() => {
    let active = true;
    apiFetch('/facilities').then(data => {
      if (active && Array.isArray(data)) {
        setFacilities(data);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const hasFacilities = facilities.length > 0;

  return (
    <>
      <section className="relative min-h-[100dvh] w-full flex items-center justify-center">
        <div className="absolute inset-0">
          <img src="/media/patal-kikian-hero-banner.webp" alt="Facilities Background" className="w-full h-full object-cover opacity-85" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        </div>
        
        <div className="relative z-10 text-center px-4 mt-20">
          <AnimatedText delay={0.2} className="text-gold uppercase tracking-[0.3em] text-sm mb-4 font-semibold">
            {t("facilities.hero.subtitle")}
          </AnimatedText>
          <AnimatedTitle
            title={t("facilities.hero.title")}
            className="text-4xl md:text-5xl lg:text-6xl font-luxury text-shadow-lg leading-tight mb-6 max-w-4xl mx-auto"
          />
        
          <AnimatedText delay={0.3}>
            <p className="text-white/80 max-w-3xl mx-auto text-lg leading-relaxed">
              {t("facilities.hero.desc")}
            </p>
          </AnimatedText>
          
        </div>
      
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
          <button 
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            className="text-white/70 hover:text-white transition-colors duration-300 flex flex-col items-center gap-2 cursor-pointer"
            aria-label="Scroll to content"
          >
            <span className="uppercase tracking-[0.2em] text-[10px]">{t("facilities.hero.btnScroll")}</span>
            <ChevronDown size={24} className="animate-bounce" />
          </button>
        </div>
      </section>

      <section className="py-32 bg-background">
        <div className="container mx-auto px-6 flex flex-col gap-32">
          {hasFacilities ? (
            facilities.map((facility: any, i: number) => (
              <div key={facility.id || i} className={`flex flex-col ${i % 2 !== 0 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-16`}>
                <div className="flex-1 w-full relative">
                  <AnimatedText delay={0.2} className="relative aspect-[4/3] w-full">
                    <ParallaxImage
                      src={facility.image || "/media/floating-breakfast-infinity-pool.webp"}
                      alt={facility.title}
                      className="w-full h-full rounded-2xl"
                    />
                  </AnimatedText>
                </div>
                <div className="flex-1 max-w-xl">
                  <AnimatedText delay={0.1}>
                    <span className="text-gold uppercase tracking-widest text-sm font-semibold">Resort Facility</span>
                  </AnimatedText>
                  <AnimatedTitle title={facility.title} className="text-4xl md:text-5xl mt-4 mb-6 font-luxury" />
                  <AnimatedText delay={0.3}>
                    <p className="text-white/70 leading-relaxed mb-8 whitespace-pre-line">
                      {facility.description}
                    </p>
                    {facility.btnLink && facility.btnText && (
                      <a href={facility.btnLink} target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-transparent border border-white/20 text-white hover:border-gold hover:text-gold transition-all duration-300 uppercase tracking-widest text-sm font-semibold">
                        {facility.btnText}
                      </a>
                    )}
                  </AnimatedText>
                </div>
              </div>
            ))
          ) : (
            <>
              {/* Fallback Static Content */}
              <div className="flex flex-col md:flex-row items-center gap-16">
                <div className="flex-1 w-full relative">
                  <AnimatedText delay={0.2} className="relative aspect-[4/3] w-full">
                    <ParallaxImage
                      src="/media/floating-breakfast-infinity-pool.webp"
                      alt="Swimming Pools"
                      className="w-full h-full rounded-2xl"
                    />
                  </AnimatedText>
                </div>
                <div className="flex-1 max-w-xl">
                  <AnimatedText delay={0.1}>
                    <span className="text-gold uppercase tracking-widest text-sm font-semibold">{t("facilities.fallback.poolSub")}</span>
                  </AnimatedText>
                  <AnimatedTitle title={t("facilities.fallback.poolTitle")} className="text-4xl md:text-5xl mt-4 mb-6 font-luxury" />
                  <AnimatedText delay={0.3}>
                    <p className="text-white/70 leading-relaxed mb-6">
                      {t("facilities.fallback.poolDesc1")}
                    </p>
                    <p className="text-white/70 leading-relaxed">
                      {t("facilities.fallback.poolDesc2")}
                    </p>
                  </AnimatedText>
                </div>
              </div>

              <div className="flex flex-col md:flex-row-reverse items-center gap-16">
                <div className="flex-1 w-full relative">
                  <AnimatedText delay={0.2} className="relative aspect-[4/3] w-full">
                    <ParallaxImage
                      src="/media/patal-kikian-restaurant (1).webp"
                      alt="Restaurant"
                      className="w-full h-full rounded-2xl"
                    />
                  </AnimatedText>
                </div>
                <div className="flex-1 max-w-xl">
                  <AnimatedText delay={0.1}>
                    <span className="text-gold uppercase tracking-widest text-sm font-semibold">{t("facilities.fallback.restSub")}</span>
                  </AnimatedText>
                  <AnimatedTitle title={t("facilities.fallback.restTitle")} className="text-4xl md:text-5xl mt-4 mb-6 font-luxury" />
                  <AnimatedText delay={0.3}>
                    <p className="text-white/70 leading-relaxed mb-6">
                      {t("facilities.fallback.restDesc1")}
                    </p>
                    <p className="text-white/70 leading-relaxed mb-8">
                      {t("facilities.fallback.restDesc2")}
                    </p>
                    <a href="/restaurant-menu/" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-transparent border border-white/20 text-white hover:border-gold hover:text-gold transition-all duration-300 uppercase tracking-widest text-sm font-semibold">
                      {t("facilities.fallback.restBtn")}
                    </a>
                  </AnimatedText>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-center gap-16">
                <div className="flex-1 w-full relative">
                  <AnimatedText delay={0.2} className="relative aspect-[4/3] w-full">
                    <ParallaxImage
                      src="/media/patal-spa-view.webp"
                      alt="Spa and Wellness"
                      className="w-full h-full rounded-2xl"
                    />
                  </AnimatedText>
                </div>
                <div className="flex-1 max-w-xl">
                  <AnimatedText delay={0.1}>
                    <span className="text-gold uppercase tracking-widest text-sm font-semibold">{t("facilities.fallback.spaSub")}</span>
                  </AnimatedText>
                  <AnimatedTitle title={t("facilities.fallback.spaTitle")} className="text-4xl md:text-5xl mt-4 mb-6 font-luxury" />
                  <AnimatedText delay={0.3}>
                    <p className="text-white/70 leading-relaxed mb-6">
                      {t("facilities.fallback.spaDesc1")}
                    </p>
                    <p className="text-white/70 leading-relaxed mb-8">
                      {t("facilities.fallback.spaDesc2")}
                    </p>
                    <a href="/spa-menu/" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-transparent border border-white/20 text-white hover:border-gold hover:text-gold transition-all duration-300 uppercase tracking-widest text-sm font-semibold">
                      {t("facilities.fallback.spaBtn")}
                    </a>
                  </AnimatedText>
                </div>
              </div>
            </>
          )}

        </div>
      </section>
    </>
  );
}
